import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export interface LoanType {
  id: number;
  group_id: number;
  name: string;
  monthly_interest: number;
  description: string;
  type: 'SIMPLE' | 'REDUCING' | 'FIXED';
  eligibility_type: 'savings_multiplier' | 'fixed_amount';
  eligibility_value: number;
  requires_guarantors?: boolean;
  guarantor_threshold_type?: 'savings_percentage' | 'loan_percentage';
  guarantor_threshold_value?: number;
  require_documents?: boolean;
  required_document_categories?: string[];
  document_requirements_description?: string;
  duration?: number;
  duration_unit?: 'days' | 'weeks' | 'months';
  formatted_duration?: string;
}

export const useLoanTypes = () => {
  return useQuery({
    queryKey: ['loan-types'],
    queryFn: async () => {
      const { data } = await api.get('/loan-type');
      return data;
    },
  });
};

export const useCreateLoanType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (loanType: Omit<LoanType, 'id'>) => {
      const { data } = await api.post('/loan-type', loanType);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-types'] });
      toast.success('Loan type created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useUpdateLoanType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...loanType }: LoanType) => {
      const { data } = await api.put(`/loan-type/${id}`, loanType);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-types'] });
      toast.success('Loan type updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useDeleteLoanType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/loan-type/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-types'] });
      toast.success('Loan type deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
}; 